import type { VerifyPersonMethod } from "@pax/api-contract/common";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { signOut } from "@/features/auth/queries";
import { useTopUp } from "@/features/credits/top-up";
import {
  clearRiskNotice,
  dismissRiskNotice,
  type RiskNotice,
  type RiskState,
  type SupportDetails,
  startCardVerification,
  useRiskState,
} from "@/lib/risk";

const SUPPORT_EMAIL = "support@pax.co";

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "numeric",
  minute: "2-digit",
});

const banReasons: Record<string, string> = {
  staff: "Suspended by Pax staff",
  moderation: "Banned from the whole site by moderation",
};

/** Today's times are a time of day; later ones name the day too. */
function formatWhen(value: string): string {
  const when = new Date(value);
  return (when.toDateString() === new Date().toDateString() ? timeFormat : dateTimeFormat).format(
    when,
  );
}

function titleOf(notice: RiskNotice): string {
  switch (notice.kind) {
    case "verify_person":
      return couldNot(notice.action);
    case "blocked":
      return "This account is banned";
    case "rate_limited":
      return "Slow down for a moment";
    case "free_credits_paused":
      return "Free credits are paused";
    case "client_allowance_spent":
      return "Free direct AI use is used up for now";
    case "acting_as_player":
      return "Not while acting as a player";
  }
}

function descriptionOf(notice: RiskNotice): string {
  switch (notice.kind) {
    case "verify_person":
      switch (notice.outcome) {
        case "card_overused":
          return "That card already verifies too many accounts. Add a different card.";
        case "card_prepaid":
          return "Prepaid cards can't verify an account. Add a different card.";
        case "not_finished":
          return "The card wasn't saved. Try adding it again.";
        default:
          return PROVE_HUMAN;
      }
    case "blocked":
      return `If you think this is a mistake, contact ${SUPPORT_EMAIL}.`;
    case "rate_limited":
      return notice.retryAt
        ? `That was a lot of requests in a short time. Try again after ${formatWhen(notice.retryAt)}.`
        : "That was a lot of requests in a short time. Try again in a minute.";
    case "free_credits_paused":
      return `So many people claimed free credits that this round is spent. They resume around ${formatWhen(notice.resetsAt)}. Your account and anything you bought are unaffected.`;
    case "client_allowance_spent":
      return `Direct AI calls outside a game have used today's free allowance. It frees up around ${formatWhen(notice.resetsAt)}. Buying credits removes the limit.`;
    case "acting_as_player":
      return "Staff acting as a player can't spend their credits or change sensitive settings. Stop acting as them to do this yourself.";
  }
}

function detailRows(details: SupportDetails): [string, string][] {
  const rows: [string, string | undefined][] = [
    ["Account", details.accountId ?? "Not signed in"],
    ["Request", details.requestId ?? "Unknown"],
    ["Time", details.at],
    ["Refusal", details.code],
    ["Step", details.step],
    ["Tier", details.tier],
    ["Ban reason", details.banCategory && (banReasons[details.banCategory] ?? details.banCategory)],
  ];
  return rows.filter((row): row is [string, string] => row[1] !== undefined);
}

function detailsText(title: string, details: SupportDetails): string {
  return [title, ...detailRows(details).map(([label, value]) => `${label}: ${value}`)].join("\n");
}

function SupportDetailsSection({ title, details }: { title: string; details: SupportDetails }) {
  const [copied, setCopied] = useState(false);
  const text = detailsText(title, details);
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Pax: ${title}`)}&body=${encodeURIComponent(`What I was doing:\n\n\n${text}`)}`;

  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
  }

  return (
    <details className="rounded-lg bg-muted/50 px-3 py-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground">Details for support</summary>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {detailRows(details).map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy details"}
        </Button>
        <Button type="button" variant="outline" size="sm" render={<a href={mailto} />}>
          Email support
        </Button>
      </div>
    </details>
  );
}

const comingSoonMethods = [
  { label: "Phone number", detail: "Coming soon", available: false },
  { label: "ID document", detail: "Coming soon", available: false },
] as const;

function VerificationMethods({ methods }: { methods: VerifyPersonMethod[] }) {
  const shown = [
    ...(methods.includes("card")
      ? [{ label: "Card", detail: "Nothing is charged", available: true }]
      : []),
    ...comingSoonMethods,
  ];
  return (
    <ul className="grid gap-1 text-sm">
      {shown.map(method => (
        <li
          key={method.label}
          className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2"
        >
          <span className={method.available ? undefined : "text-muted-foreground"}>
            {method.label}
          </span>
          <span className="text-xs text-muted-foreground">{method.detail}</span>
        </li>
      ))}
    </ul>
  );
}

/** Every step-up says what couldn't be done, from the refused route's summary, then asks for proof. */
function couldNot(action: string): string {
  return `Couldn't ${action.charAt(0).toLowerCase()}${action.slice(1)}`;
}

const PROVE_HUMAN = "Please prove you're a human.";

/** The one place the app explains why the API refused something, mounted once at the root. */
export function RiskDialog() {
  const state = useRiskState();
  if (!state) return null;
  return <RiskNoticeDialog key={`${state.details.at}:${state.details.code}`} state={state} />;
}

function RiskNoticeDialog({ state: { notice, details } }: { state: RiskState }) {
  const queryClient = useQueryClient();
  const { openTopUp } = useTopUp();
  const addCard = useMutation({
    mutationFn: () =>
      startCardVerification(notice.kind === "verify_person" ? notice.action : "Claim free credits"),
  });
  const leave = useMutation({
    mutationFn: () => signOut(queryClient),
    onSettled: clearRiskNotice,
  });
  const blocked = notice.kind === "blocked";
  const title = titleOf(notice);
  const cardAvailable = notice.kind === "verify_person" && notice.methods.includes("card");

  return (
    <Dialog
      open
      disablePointerDismissal={blocked}
      onOpenChange={open => {
        if (!open && !blocked) dismissRiskNotice();
      }}
    >
      <DialogContent nestedBackdrop showCloseButton={!blocked}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {addCard.isError
              ? "Stripe didn't open. Try adding the card again."
              : descriptionOf(notice)}
          </DialogDescription>
        </DialogHeader>

        {notice.kind === "verify_person" && <VerificationMethods methods={notice.methods} />}
        <SupportDetailsSection title={title} details={details} />

        <DialogFooter className="flex-row flex-wrap justify-end">
          {notice.kind === "verify_person" ? (
            <>
              <Button type="button" variant="outline" onClick={dismissRiskNotice}>
                Not now
              </Button>
              {cardAvailable && (
                <Button type="button" disabled={addCard.isPending} onClick={() => addCard.mutate()}>
                  {addCard.isPending ? "Opening Stripe…" : "Add a card"}
                </Button>
              )}
            </>
          ) : blocked ? (
            <Button type="button" disabled={leave.isPending} onClick={() => leave.mutate()}>
              {leave.isPending ? "Signing out…" : "Sign out"}
            </Button>
          ) : notice.kind === "client_allowance_spent" ? (
            <>
              <Button type="button" variant="outline" onClick={dismissRiskNotice}>
                Not now
              </Button>
              <Button
                type="button"
                onClick={() => {
                  dismissRiskNotice();
                  openTopUp();
                }}
              >
                Buy credits
              </Button>
            </>
          ) : (
            <Button type="button" onClick={dismissRiskNotice}>
              OK
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
